/**
 * GLOBAL SØGNING ([D-198], Charlies bestilling 17-08-2026).
 *
 * Problemet: appen har så mange nøgletal, charts og lister, at man skal vide
 * præcis hvor man skal kigge for at finde noget. Søgningen samler derfor fire
 * slags mål i én liste:
 *
 *   1. SIDER      — fra side-kataloget (samme kilde som menuen).
 *   2. AFSNIT     — genereret fra `<SectionTitle>` i view-filerne.
 *   3. NØGLETAL   — genereret fra `<KpiCard label="…">`.
 *   4. ORDBOGEN   — opslag fra glossary-terms, så et fagord fører til sin
 *                   definition (ordbogen har allerede ankre pr. term).
 *
 * ⚠ RETTIGHEDER ER FAIL-CLOSED ([D-141]): hvert resultat filtreres på
 * `canAccessPath`, PRÆCIS som menuen. Uden det ville søgefeltet blive en
 * bagvej til at se hvilke sider og nøgletal der findes på sider man ikke har
 * adgang til — titlerne alene kan røbe forretningsindhold (fx at der findes et
 * afsnit "Finansielle nøgletal"). Indekset er statisk og ligger i bundlet, så
 * filtreringen SKAL ske her ved hvert opslag.
 *
 * Rangeringen er bevidst simpel og forudsigelig frem for smart: eksakt match →
 * ordet starter med søgeteksten → indeholder søgeteksten, og sider vinder over
 * afsnit ved lige score, fordi en side er det bredeste svar på et vagt ord.
 */

import { canAccessPath } from '../composables/usePermissions';
import type { RoleCode } from '../permissions';
import { GENERATED_SEARCH_ENTRIES } from './search-index.generated';
import { GLOSSARY_TERMS } from './glossary-terms';

export type SearchKind = 'page' | 'section' | 'metric' | 'term';

export interface SearchHit {
  /** Hvad brugeren læser. */
  title: string;
  /** Kontekst-linje ("Marketing · Blended", "Ordbogen"). */
  context: string;
  kind: SearchKind;
  /** Rute at navigere til. */
  path: string;
  /** Element-id der skal scrolles til, hvis træffet er et afsnit/opslag. */
  anchor?: string;
  score: number;
}

const KIND_LABEL: Record<SearchKind, string> = {
  page: 'Side',
  section: 'Afsnit',
  metric: 'Nøgletal',
  term: 'Ordbogen',
};

export function kindLabel(kind: SearchKind): string {
  return KIND_LABEL[kind];
}

/** Ordbogens egne ankre — SAMME regel som GlossaryDocView.slug(). */
function glossaryAnchor(term: string): string {
  return (
    'ord-' +
    term
      .toLowerCase()
      .replace(/[^a-z0-9æøå]+/g, '-')
      .replace(/^-+|-+$/g, '')
  );
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFC')
    .replace(/[æ]/g, 'ae')
    .replace(/[ø]/g, 'oe')
    .replace(/[å]/g, 'aa')
    .trim();
}

/**
 * Score for ét felt mod søgeteksten. 0 = intet match.
 *
 * Ordgrænse-matchet ("starter et ord med q?") er vigtigt for forkortelser:
 * "roas" skal ramme "Gross ROAS" lige så tydeligt som "ROAS-udvikling".
 */
function scoreText(text: string, q: string): number {
  const t = normalize(text);
  if (t.length === 0) return 0;
  if (t === q) return 100;
  if (t.startsWith(q)) return 70;
  const words = t.split(/[^a-z0-9~]+/);
  if (words.some((w) => w === q)) return 65;
  if (words.some((w) => w.startsWith(q))) return 50;
  if (t.includes(q)) return 30;
  return 0;
}

/** Bonus pr. type, så et vagt ord lander på det bredeste svar først. */
const KIND_BONUS: Record<SearchKind, number> = {
  page: 6,
  section: 3,
  metric: 2,
  term: 0,
};

export interface CatalogPage {
  path: string;
  title: string;
  section: string;
}

/**
 * Søg. `pages` er side-kataloget (fra usePermissions), `roles` brugerens roller.
 * Tom/kort søgetekst giver ingen resultater — ét bogstav ville matche alt og
 * gøre listen ubrugelig.
 */
export function searchAll(
  rawQuery: string,
  pages: readonly CatalogPage[],
  roles: RoleCode[],
  limit = 40,
): SearchHit[] {
  const q = normalize(rawQuery);
  if (q.length < 2) return [];

  const allowed = (path: string): boolean => canAccessPath(path, roles);
  const hits: SearchHit[] = [];

  // 1. Sider
  for (const p of pages) {
    if (!allowed(p.path)) continue;
    const score = Math.max(scoreText(p.title, q), scoreText(p.section, q) - 20);
    if (score > 0) {
      hits.push({
        title: p.title,
        context: p.section,
        kind: 'page',
        path: p.path,
        score: score + KIND_BONUS.page,
      });
    }
  }

  // 2+3. Afsnit og nøgletal (genereret fra koden)
  const pageTitleByPath = new Map(pages.map((p) => [p.path, p]));
  for (const e of GENERATED_SEARCH_ENTRIES) {
    if (!allowed(e.path)) continue;
    const score = scoreText(e.title, q);
    if (score <= 0) continue;
    const cat = pageTitleByPath.get(e.path);
    const context = cat ? `${cat.section} · ${cat.title}` : e.pageTitle;
    hits.push({
      title: e.title,
      context,
      kind: e.kind,
      path: e.path,
      anchor: e.anchor,
      score: score + KIND_BONUS[e.kind],
    });
  }

  // 4. Ordbogen — kun hvis brugeren må se den side.
  if (allowed('/doc/glossary')) {
    for (const t of GLOSSARY_TERMS) {
      const score = Math.max(
        scoreText(t.term, q),
        t.full ? scoreText(t.full, q) - 5 : 0,
        ...(t.aliases ?? []).map((a) => scoreText(a, q) - 5),
      );
      if (score <= 0) continue;
      hits.push({
        title: t.term,
        context: t.full ? `Ordbogen · ${t.full}` : 'Ordbogen',
        kind: 'term',
        path: '/doc/glossary',
        anchor: glossaryAnchor(t.term),
        score: score + KIND_BONUS.term,
      });
    }
  }

  // Samme titel på samme sti to gange (fx et nøgletal der også er afsnit) —
  // behold det bedst scorende.
  const best = new Map<string, SearchHit>();
  for (const h of hits) {
    const key = `${h.path}|${h.anchor ?? ''}|${normalize(h.title)}`;
    const prev = best.get(key);
    if (!prev || h.score > prev.score) best.set(key, h);
  }

  return [...best.values()]
    .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title, 'da-DK'))
    .slice(0, limit);
}
